import React, { useEffect, useRef, useState } from "react";
import { ErrorToast, SuccessToast } from "../../../../Utils/SweetAlert";
import { useMutateUpdateSettingsDetails } from "../hooks/useApihook";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import InputWithLabel from "../../../../Components/InputWithLabel";
import { useQuery } from "@tanstack/react-query";
import { GetSettingsDetails } from "../../../../Api/setting";
import SaveButton from "../../../../Components/SaveButton";

const ClearThreadMessageLimitSchema = Yup.object().shape({
    limit: Yup.number()
        .required("Limit is required")
        .min(1, "Limit must be at least 1")
        .typeError("Limit must be a number"),
});

const ClearThreadMessageLimit = () => {
    const updateSettingsMutation = useMutateUpdateSettingsDetails();
    const formRef = useRef();
    const [initialValue, setInitialValue] = useState(null);

    const { data: settingsData } = useQuery({
        queryKey: ["get-settings-details", "clearThreadMessageLimit"],
        queryFn: () => GetSettingsDetails("clearThreadMessageLimit"),
        throwOnError: true,
        onError: (error) => {
            ErrorToast(
                error?.response?.data?.message || error?.message || "Server Error."
            );
        },
    });

    useEffect(() => {
        if (settingsData) {
            const limitValue = settingsData?.data?.limit || 10;
            setInitialValue(limitValue);
            if (formRef?.current?.values) {
                formRef?.current?.setValues({
                    limit: limitValue,
                });
            }
        }
    }, [settingsData]);

    const handleUpdate = (values, { setSubmitting }) => {
        const reqData = {
            name: "clearThreadMessageLimit",
            details: {
                limit: values.limit,
            },
        };

        updateSettingsMutation.mutate(reqData, {
            onSuccess: (response) => {
                SuccessToast(response.message || "Thread message limit updated successfully.");
                setInitialValue(values.limit);
                setSubmitting(false);
            },
            onError: (error) => {
                ErrorToast(error?.response?.data?.message || "Server Error.");
                setSubmitting(false);
            },
        });
    };

    return (
        <div className="setting-border overflow-visible">
            <div className="">
                <div className="row">
                    <div className="col-md-12">
                        <div className="alert alert-info" role="alert">
                            Set the limit for stored messages after a thread needs to be cleared.
                        </div>
                        <section className="">
                            <Formik
                                initialValues={{ limit: initialValue || "" }}
                                validationSchema={ClearThreadMessageLimitSchema}
                                validateOnChange
                                innerRef={formRef}
                                onSubmit={handleUpdate}
                                enableReinitialize
                            >
                                {(formik) => {
                                    const { isSubmitting, values, isValid, dirty } = formik;
                                    // Enable save button only if value has changed and is valid
                                    const isSaveDisabled = !dirty || !isValid || isSubmitting;

                                    return (
                                        <Form>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Field
                                                        Label="Clear Thread Message Limit"
                                                        type="number"
                                                        name="limit"
                                                        placeholder="Enter message limit"
                                                        min="1"
                                                        component={InputWithLabel}
                                                    />
                                                </div>
                                            </div>
                                            <SaveButton loading={isSubmitting} disabled={isSaveDisabled} />
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClearThreadMessageLimit;
